import {store} from "../store";
import {isDefined} from "../../util/is-type";
import {deviceSlice, IDeviceStore} from "./deviceSlice";
import {IDeviceInfo} from "../../common/interfaces/IDeviceInfo";
import deviceInfo from "../../services/deviceInfo/deviceInfo";

const phonePortraitMaxWidth = `480px`;
const phoneLandscapeMaxWidth = `1040px`;
const phoneLandscapeMediaQuery = `only screen and (orientation: landscape) and (width <= ${phoneLandscapeMaxWidth})`;
const phonePortraitMediaQuery = `only screen and (orientation: portrait) and (width <= ${phonePortraitMaxWidth}) `;

const deviceOsValuesForWindows = [
  `Windows`,
  `win32`,
];

const deviceOsValuesForMac = [
  `Mac OS`, // Modern iPads use a UA string in the browser/WebView which is indistinguishable from macOS.
  `darwin`,
];

const deviceOsValuesForIOs = [
  `iOS`,
];

export const deviceStore = {
  // State modifiers
  setMockedDevice: (mockedDevice: IDeviceInfo)=> store.dispatch(deviceSlice.actions.mockedDeviceReducer(mockedDevice)),
  setDevice: (device: IDeviceStore) => { store.dispatch((deviceSlice.actions.deviceReducer(device))); },
  setDeviceAsDesktopWindows: () => {
    deviceStore.setMockedDevice({
      deviceInfoVersion:"v2",
      os:"win32",
      osVersion:"10.0.22000",
      isPad:false,
      isDesktop:true,
    });
  },
  setDeviceAsDesktopMac: () => {
    deviceStore.setMockedDevice({
      deviceInfoVersion:"v2",
      os:"darwin",
      osVersion:"10.0.0",
      isPad:false,
      isDesktop:true,
    });
  },
  setDeviceAsMobileIPhone: () => {
    deviceStore.setMockedDevice({
      deviceInfoVersion:"v2",
      os:"iOS",
      osVersion:"10.0.22000",
      isPad:false,
      isDesktop:false,
      isMobile: true,
    });
  },
  setDeviceAsTabletIPad: () => {
    deviceStore.setMockedDevice({
      deviceInfoVersion:"v2",
      os:"iOS",
      osVersion:"10.0.22000",
      isPad:true,
      isDesktop:false,
      isMobile: false,
    });
  },
  setDeviceAsAndroid: () => {
    deviceStore.setMockedDevice({
      deviceInfoVersion: "v2",
      os: "android",
      osVersion:"10.0.22000",
      isPad:false,
      isDesktop:false,
      isMobile:true,
    });
  },

  // Getters
  getDeviceIsDesktop: () => {
    const hardware = deviceSlice.selectors.deviceHardwareSelector(store.getState());
    const os = deviceStore.getDeviceOs();

    // Check native app info first if available
    if (window.deviceInfo?.isDesktop !== undefined) {
      return window.deviceInfo.isDesktop;
    }

    // iOS and Android are mobile/tablet platforms, not desktop
    if (os === "iOS" || deviceInfo.getIsAndroid(os)) {
      return false;
    }

    // For Mac OS and Windows, check if it's NOT a mobile or tablet device
    if (os === `Mac OS` || os === `darwin` || os === `Windows` || os === `win32`) {
      // Check if it's a mobile device
      const isMobile = deviceStore.getDeviceIsMobile();
      const isTablet = deviceStore.getDeviceIsTablet();

      // Desktop if it's not mobile and not tablet
      const isDesktop = !isMobile && !isTablet;
      return isDesktop;
    }

    // For unknown OS, assume desktop if device type is not mobile/tablet
    const deviceType = hardware.type;
    return deviceType !== 'mobile' && deviceType !== 'tablet';
  },
  getDeviceIsMacDesktop: ()=> {
    const isDesktop = deviceStore.getDeviceIsDesktop();
    const isOSMac = deviceStore.getDeviceOsIsMac();
    return isDesktop && isOSMac;
  },
  getDeviceIsWindowsDesktop: ()=> {
    const isDesktop = deviceStore.getDeviceIsDesktop();
    const isOSWindows = deviceStore.getDeviceOsIsWindows();
    return isDesktop && isOSWindows;
  },
  getDeviceIsTablet: ()=> {
    const info = deviceSlice.selectors.deviceInfoSelector(store.getState());
    const hardware = deviceSlice.selectors.deviceHardwareSelector(store.getState());

    const isIOS = deviceStore.getDeviceOsIsIOS();

    // For iOS, check if it's an iPad
    if (isIOS) {
      return info?.isPad === true;
    }

    // For Android and other platforms, check device type from UAParser
    // UAParser detects tablets as type 'tablet'
    const deviceType = hardware.type;
    return deviceType === 'tablet';
  },
  getDeviceOsIsWindows: () => {
    const os = deviceSlice.selectors.deviceOsSelector(store.getState());
    return isDefined(os) && deviceOsValuesForWindows.includes(os);
  },
  /** Perhaps surprisingly, this is true on modern iPads, since they share UA strings with macOS. */
  getDeviceOsIsMac: ()=> {
    const os = deviceSlice.selectors.deviceOsSelector(store.getState());
    return isDefined(os) && deviceOsValuesForMac.includes(os);
  },
  /** This is false for modern iPads, which run iPadOS & have UA strings which are indistinguishable from macOS.*/
  getDeviceOsIsIOS: ()=> {
    const os = deviceSlice.selectors.deviceOsSelector(store.getState());
    return isDefined(os) && deviceOsValuesForIOs.includes(os);
  },
  /** Whether Redux-stored OS is Android (passes stored os into deviceInfo.getIsAndroid). */
  getDeviceOsIsAndroid: ()=> {
    const os = deviceSlice.selectors.deviceOsSelector(store.getState());
    return deviceInfo.getIsAndroid(os);
  },
  getDeviceOsVersion: ()=> deviceSlice.selectors.deviceOsVersionSelector(store.getState()),
  getDeviceOs: ()=> deviceSlice.selectors.deviceOsSelector(store.getState()),
  getDeviceIsMobile: ()=> {
    const deviceInfo = window.deviceInfo;
    if (!deviceInfo || deviceStore.getDeviceOsIsAndroid()) {
      return isPhone();
    }

    //web can be mismatched with native release, so check if the data necessary to detect mobile is provided in this version of the native app.
    if (deviceInfo.deviceInfoVersion !== "v2") {
      return false;
    }

    /**
     * `isPad` is iOS-only. It won't exist for Android, nor is there an equivalent.
     * @link https://reactnative.dev/docs/platform#ispad-ios
     */
    if (deviceInfo.isPad) {
      return false;
    }

    return deviceStore.getDeviceOsIsIOS();
  },
  getDeviceIsIPad:          () => deviceStore.getDeviceOsIsIOS()     &&  deviceStore.getDeviceIsTablet(),
  getDeviceIsIPhone:        () => deviceStore.getDeviceOsIsIOS()     && !deviceStore.getDeviceIsTablet(),
  getDeviceIsAndroidTablet: () => deviceStore.getDeviceOsIsAndroid() &&  deviceStore.getDeviceIsTablet(),
  getDeviceIsAndroidPhone:  () => deviceStore.getDeviceOsIsAndroid() && !deviceStore.getDeviceIsTablet(),
  getDeviceFormFactor: () => {
    if (deviceStore.getDeviceIsTablet()) { return 'tablet'; }
    if (deviceStore.getDeviceIsDesktop()) { return 'desktop'; }
    if (deviceStore.getDeviceIsAndroidPhone() || deviceStore.getDeviceIsIPhone()) { return 'phone'; }
    return 'unknown';
  },
  getDeviceBrowserName: ()=> deviceSlice.selectors.deviceBrowserNameSelector(store.getState()),
}

/**
 * Detects if the device is a phone across all platforms
 * Uses a multi-layered approach:
 * 1. OS detection (phones only run iOS or Android)
 * 2. UAParser device type detection
 * 3. Excludes tablets (tablets are mobile but not phones)
 * 4. Falls back to screen size as last resort
 */
function isPhone(): boolean {
  const hardware = deviceSlice.selectors.deviceHardwareSelector(store.getState());

  const os = deviceStore.getDeviceOs();

  // Phones only run on mobile operating systems
  // Desktop OSes (Windows, Mac OS) are never phones
  if (os !== "iOS" && !deviceInfo.getIsAndroid(os)) {
    return false;
  }

  // For iOS, check if it's NOT an iPad (iPad is tablet, not phone)
  if (os === "iOS") {
    // If native app provides isPad info, use it
    if (window.deviceInfo?.isPad !== undefined) {
      return !window.deviceInfo.isPad;
    }
  }

  // Use UAParser device type detection
  const deviceType = hardware.type;

  // If UAParser detected it as mobile (not tablet), it's a phone
  if (deviceType === 'mobile') {
    return true;
  }

  // If UAParser detected it as tablet, it's not a phone
  if (deviceType === 'tablet') {
    return false;
  }

  // Fallback: Use screen size detection for cases where UAParser
  // doesn't provide device type (some Android browsers)
  // This is less reliable but better than nothing
  const phonePortrait = window.matchMedia(phonePortraitMediaQuery);
  const phoneLandscape = window.matchMedia(phoneLandscapeMediaQuery);
  return phonePortrait.matches || phoneLandscape.matches;
}